import { useState } from 'owa-react';
import { observer } from 'owa-mobx-react';
import { MailListFilterMenu } from 'owa-mail-list-filter-view';
import { MailListSortMenuV2 } from 'owa-mail-list-sort-view';
import { MailListJumpToPopover, shouldShowJumpTo } from 'owa-mail-list-jumpto-view';
import {
    getStore as getListViewStore,
    getIsSearchTableShown,
    getComputedSelectedRowKeysSizeForTable,
    getUserCategoryName,
    type MailFolderTableQuery,
    isCatchUpFilter,
    isCopilotResultsTable,
    isCopilotResultsTabVisible,
    isHeadlinesFilter,
    isPriorityView,
    isNeedsReplyView,
} from 'owa-mail-list-store';
import type { ActionSource } from 'owa-mail-store';
import React from 'react';
import type ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { isFocusedInboxEnabled } from 'owa-mail-triage-common';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { getDensityModeCssClass, getDensityModeString } from 'owa-fabric-theme';
import { isSingleLineListView, isReadingPanePositionOff } from 'owa-mail-layout';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getIsNonMailEntitySearchTab } from 'owa-tabbed-search';
import { getMailboxInfoFromTableViewId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import { type MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import { getMailListLoadState } from 'owa-mail-list-store/lib/utils/getMailListLoadState';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import shouldHideMessageListCheckboxes from 'owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckboxes';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import {
    full,
    medium,
    compact,
    SLVItemViewSenderImageOn,
    SLVItemViewSenderImageOff,
    SLVConversationViewSenderImageOn,
    SLVConversationViewSenderImageOff,
    threeColItemViewSenderImageOn,
    threeColItemViewSenderImageOff,
    threeColConversationViewSenderImageOn,
    threeColConversationViewSenderImageOff,
    mailListSecondRowLeftEntity,
    secondHeaderActionContainer,
    selectAllCheckboxContainerSearchTable,
    mailListHeaderContainer,
    mailListHeaderContainerBleedThrough,
    mailListThirdRowMCL,
    mailListThirdRowSCL,
    mailListFirstRow,
    mailListSecondRow,
    mailListSecondRowDenseless,
    mailSearchSecondRowDenseless,
    mailListSecondRowCompact,
    mailListSecondRowSemiCompact,
    mailListSecondRowInterplayEnabled,
    mcl,
    scl,
    thirdrowPaddingBottom,
    withFirstRow,
    mailListSecondRowInnerContent,
    mailListSecondRowInnerContentSearchTable,
    notesHeaderSecondRow,
    stickyNotesHeaderPadding,
    mailListSecondRowCustomContainer,
    secondRowCustomContainer,
    selectAllHeaderStyle,
    conversationViewMCL,
    bulkActionBarPaddingRuleProgress,
    checkboxSpaceReserved,
    senderImageOn,
    triMlCheckboxBehaviorSenderImageOn,
    triMlCheckboxBehaviorSenderImageOff,
    senderImageOff,
    showInboxPivots,
    noInboxPivots,
    headerIconContainer,
    headerIconContainerBleedThrough,
    headerIconContainerFull,
    headerIconContainerMediumOrCompact,
    copilotResultsTabVisible,
    mlHeaderButtonsFull,
    mlHeaderButtonsSemiCompact,
    mlHeaderButtonsCompact,
    compactDenseless,
    mediumDenseless,
    monTypeRampStyles,
} from './MailListHeader.scss';
import { isPrioritizeOnDemandOn } from 'owa-copilot-settings-utils';
import { isCapabilityEnabled } from 'owa-capabilities';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import {
    DEFAULT_LIST_VIEW_WIDTH,
    MIN_LV_WIDTH_WITH_FULL_HEADER_BUTTONS,
} from 'owa-mail-layout/lib/mailLayoutConstants';

import type { UserSettingsStyleSelector } from '../utils/getStyleSelectorAsPerUserSettings';
import classnames from 'owa-classnames';
import SelectAllCheckbox from './SelectAllCheckbox';
import MailListHeaderIconContainer from './MailListHeaderIconContainer';
import { CollapseDelayed } from '@fluentui/react-motion-components-preview';
import {
    PriorityViewBar,
    PriorityViewHeaderButtons,
    shouldShowPrioritizeButton,
    PrioritizeStatusBar,
} from 'owa-mail-priority-inbox';
import { MailListNudgeContainer } from '../lazyFunctions';
import { getUserStore, PrioritizeRequestStatus } from 'owa-mail-priority-inbox-store';
import { NeedsReplyBar, NeedsReplyViewHeaderButtons } from 'owa-mail-needs-reply';

export interface MailListHeaderProps {
    filterMenuSource: ActionSource;
    shouldShowFilterMenu: boolean;
    tableViewId: string;
    renderFirstRow?: (mailListRowCss: string) => JSX.Element;
    renderSecondRowCustomContent?: (customContainerCss: string) => JSX.Element;
    renderThirdRow?: (mailListThirdRowCss: string) => JSX.Element;
    renderBulkActionProgressBar?: () => JSX.Element;
    mailListHeaderStylesAsPerUserSettings?: UserSettingsStyleSelector;
}

enum ML_HEADER_BUTTON_STATE {
    FULL, // Full header buttons (e.g. label + icon)
    SEMI_COMPACT, // Semi-compact header buttons (e.g. label + icon for sort button, but no label for all other buttons)
    COMPACT,
}

//  Header Structure
// ---------------------------------------------------------------------------------------------------
// |  Mail List Header Container                                                                     |
// |  ---------------------------------------------------------------------------------------------  |
// |  | First Row (Custom - renders optionally)                                                   |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Second Row (Half Custom - renders always)                                                 |  |
// |  | ---------- -----------------------------                        --------------------------|  |
// |  | |  Check | |           Custom          |                        |     Filter Dropdown    ||  |
// |  | |   Box  | |           Content         |                        |    (based on scenario) ||  |
// |  | ---------- -----------------------------                        --------------------------|  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Third Row (Custom - renders optionally)                                                   |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Bulk action progress bar (Custom - renders optionally)                                    |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// ---------------------------------------------------------------------------------------------------

const defaultExport: React.FunctionComponent<MailListHeaderProps> = observer(
    function MailListHeader(props: MailListHeaderProps) {
        const mailboxInfo = getMailboxInfoFromTableViewId(props.tableViewId);
        const tableView = getListViewStore().tableViews.get(props.tableViewId);
        const isSingleLineView = isSingleLineListView();
        const isItemView = tableView?.tableQuery?.listViewType === 1;
        const isSearchTable = getIsSearchTableShown();
        const isCatchUpFilterSelected = isCatchUpFilter(
            tableView?.tableQuery as MailFolderTableQuery
        );
        const senderImageEnabled = isSenderImageEnabled();
        const areMessageListCheckBoxesHidden = shouldHideMessageListCheckboxes();
        const isReselectCheckboxBehaviorEnabled = isGlobalFeatureEnabled(
            'tri-reselect-checkbox-behavior'
        );
        const isCatchUpOrHeadlinesFilterSelected =
            isCatchUpFilterSelected ||
            isHeadlinesFilter(tableView?.tableQuery as MailFolderTableQuery);
        const showMultiSelectButton =
            !isSearchTable ||
            (isCatchUpOrHeadlinesFilterSelected && !areMessageListCheckBoxesHidden);
        const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
        const densityModeString = getDensityModeString();
        const densityModeClass = isRPDenselessEnabled
            ? getDensityModeCssClass(full, medium, medium)
            : getDensityModeCssClass(full, medium, compact);
        const isSelectionModeActivated = isSingleLineView
            ? tableView?.isInSelectAllStateSLV
            : tableView?.isInSelectAllState;
        const showCheckbox = isSelectionModeActivated || tableView?.isInCheckedMode;
        const showCheckboxForReselectFlight =
            isGlobalFeatureEnabled('tri-reselect-checkbox-behavior') &&
            (isSingleLineView ||
                senderImageEnabled ||
                (!tableView?.isOnlyOptimisticallyInCheckedMode && showCheckbox) ||
                (tableView?.isOnlyOptimisticallyInCheckedMode &&
                    showCheckbox &&
                    senderImageEnabled));
        const checkedStateMailList =
            (!isGlobalFeatureEnabled('tri-reselect-checkbox-behavior') && showCheckbox) ||
            showCheckboxForReselectFlight;
        const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
        const mailListViewState = getMailListLoadState();
        const isEmptyFolder = mailListViewState === 'Empty';
        const shouldShowSelectionHeader =
            isReadingPanePositionOff() &&
            ((tableView?.isInCheckedMode &&
                (isGlobalFeatureEnabled('fwk-ml-reduce-render-scope')
                    ? getComputedSelectedRowKeysSizeForTable(props.tableViewId) > 0
                    : (tableView?.selectedRowKeys.size ?? 0) > 0)) ||
                tableView?.isInVirtualSelectAllMode);
        const headerRef = React.useRef<HTMLDivElement>(null);

        const categoryName = tableView ? getUserCategoryName(tableView) : null;
        const isCategoryFolder = categoryName !== null;

        const [mlHeaderButtonState, setMlHeaderButtonState] = useState<ML_HEADER_BUTTON_STATE>(
            ML_HEADER_BUTTON_STATE.COMPACT
        );

        const shouldShowPrioritize =
            shouldShowPrioritizeButton(props.tableViewId) &&
            props.filterMenuSource !== 'GroupHeader';

        const shouldHideNonPriorityHeaderButtons =
            isPrioritizeOnDemandOn(mailboxInfo) &&
            isPriorityView(tableView?.tableQuery as MailFolderTableQuery);

        const isInNeedsReplyView = isNeedsReplyView(tableView?.tableQuery as MailFolderTableQuery);

        useResizeObserver('MailListHeader', headerRef, (bounds: DOMRectReadOnly) => {
            // When in MCL view:
            // If the width is less than DEFAULT_LIST_VIEW_WIDTH (350px) + 100px, we show the compact (icon only) header buttons.
            // If the width is less than MIN_LV_WIDTH_WITH_FULL_HEADER_BUTTONS (625px), we show the semi-compact (some buttons show icon only while others show icon + label) header buttons.
            // Else, we show the full (icon + label) header buttons.
            if (isSingleLineView) {
                if (bounds.width < DEFAULT_LIST_VIEW_WIDTH + 100) {
                    setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.COMPACT);
                } else if (bounds.width < MIN_LV_WIDTH_WITH_FULL_HEADER_BUTTONS) {
                    setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.SEMI_COMPACT);
                } else {
                    setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.FULL);
                }
            } else {
                // When in SCL view:
                // If the width is less than DEFAULT_LIST_VIEW_WIDTH (350px) + 100px, we show the compact (icon only) header buttons.
                // Else, we show the semi-compact (some buttons show icon only while others show icon + label) header buttons.
                if (bounds.width < DEFAULT_LIST_VIEW_WIDTH + 100) {
                    setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.COMPACT);
                } else {
                    setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.SEMI_COMPACT);
                }
            }
        });

        const showFullLabels = mlHeaderButtonState === ML_HEADER_BUTTON_STATE.FULL;
        const showSemiCompactLabels = mlHeaderButtonState === ML_HEADER_BUTTON_STATE.SEMI_COMPACT;

        const classNameForUserSettings =
            props.mailListHeaderStylesAsPerUserSettings &&
            (
                {
                    SLVItemViewSenderImageOn,
                    SLVItemViewSenderImageOff,
                    SLVConversationViewSenderImageOn,
                    SLVConversationViewSenderImageOff,
                    threeColItemViewSenderImageOn,
                    threeColItemViewSenderImageOff,
                    threeColConversationViewSenderImageOn,
                    threeColConversationViewSenderImageOff,
                } as const
            )[props.mailListHeaderStylesAsPerUserSettings];

        const renderSecondRowLeftEntity = (
            isInboxWithPivots: boolean,
            isStickyNotesHeader: boolean
        ) => {
            const canShowSelectAllCheckbox =
                showCheckboxForReselectFlight || !isReselectCheckboxBehaviorEnabled;
            const shouldShowSelectAllCheckbox =
                canShowSelectAllCheckbox && !areMessageListCheckBoxesHidden;
            // Notes has no checkbox, while non-mail search tabs require a placeholder to avoid jumpy UI
            if (isStickyNotesHeader) {
                return null;
            } else if (getIsNonMailEntitySearchTab()) {
                return <div className={mailListSecondRowLeftEntity} />;
            } else if (!isEmptyFolder && shouldShowSelectAllCheckbox) {
                return (
                    <div
                        className={classnames(
                            secondHeaderActionContainer,
                            isSingleLineView ? mcl : scl,
                            isInboxWithPivots && !shouldShowSelectionHeader
                                ? showInboxPivots
                                : noInboxPivots,
                            densityModeClass,
                            isRPDenselessEnabled &&
                                densityModeString === 'compact' &&
                                compactDenseless,
                            isRPDenselessEnabled &&
                                densityModeString === 'medium' &&
                                mediumDenseless,
                            isSearchTable && selectAllCheckboxContainerSearchTable
                        )}
                    >
                        <SelectAllCheckbox
                            tableViewId={props.tableViewId}
                            isInboxWithPivots={isInboxWithPivots}
                        />
                    </div>
                );
            } else {
                if (isSearchTable) {
                    return (
                        <div
                            className={classnames(
                                shouldShowSelectAllCheckbox && secondHeaderActionContainer,
                                isSingleLineView ? mcl : scl,
                                isInboxWithPivots && !shouldShowSelectionHeader
                                    ? showInboxPivots
                                    : noInboxPivots,
                                densityModeClass,
                                isRPDenselessEnabled &&
                                    densityModeString === 'compact' &&
                                    compactDenseless,
                                isRPDenselessEnabled &&
                                    densityModeString === 'medium' &&
                                    mediumDenseless
                            )}
                        />
                    );
                } else {
                    return;
                }
            }
        };

        const renderMailListFilter = () => {
            const { filterMenuSource, tableViewId } = props;
            const supportedViewFilters = getViewFilters();
            return (
                <MailListFilterMenu
                    filterMenuSource={filterMenuSource}
                    supportedViewFilters={supportedViewFilters}
                    tableViewId={tableViewId}
                    showButtonLabel={showFullLabels}
                    isBleedThrough={isRPDenselessEnabled}
                />
            );
        };

        const mailListThirdRowCss = classnames(
            isSingleLineView ? mailListThirdRowMCL : mailListThirdRowSCL,
            classNameForUserSettings
        );
        const isCopilotResultsTableSelected =
            !!tableView && isCopilotResultsTable(tableView.tableQuery);
        const isBizchatMLInterplayEnabled = isGlobalFeatureEnabled(
            'mon-copilot-interplay-bizchat-ML'
        );
        const folderId = tableView?.tableQuery?.folderId ?? '';
        const isInbox = folderIdToName(folderId) === 'inbox';
        // While prioritization is running, ready, or has failed (but not yet auto-opened into
        // Priority View), we replace all header nudges with the status bar.
        const prioritizeStatus = getUserStore(mailboxInfo).prioritizeRequestStatus;
        const isPrioritizeActive =
            isGlobalFeatureEnabled('tri-prioritize-on-demand') &&
            (prioritizeStatus === PrioritizeRequestStatus.InProgress ||
                prioritizeStatus === PrioritizeRequestStatus.Ready ||
                prioritizeStatus === PrioritizeRequestStatus.Error);
        const isInboxWithPivots =
            ((isFocusedInboxEnabled(mailboxInfo) && isInbox) ||
                (isBizchatMLInterplayEnabled &&
                    (!isSearchTable || isCopilotResultsTabVisible(mailboxInfo)))) &&
            !isPriorityView(tableView?.tableQuery as MailFolderTableQuery);
        const isStickyNotesHeader =
            folderIdToName(folderId) === 'notes' && isGlobalFeatureEnabled('notes-folder-view');
        const isLegacyNotesHeader =
            folderIdToName(folderId) === 'notes' && !isGlobalFeatureEnabled('notes-folder-view');
        if (!props.renderSecondRowCustomContent) {
            throw new Error('MailListHeader: renderSecondRowCustomContent should not be null');
        }
        const thirdRow = props.renderThirdRow?.(mailListThirdRowCss);
        const firstRow = props.renderFirstRow?.(mailListFirstRow);
        const isCheckboxSpaceReserved =
            isSearchTable ||
            checkedStateMailList ||
            isCategoryFolder ||
            isLegacyNotesHeader ||
            !props.shouldShowFilterMenu;
        const shouldApplyNewNoCheckboxStyles = isGlobalFeatureEnabled('tri-mlCheckboxBehavior-dev');
        return (
            <div
                className={classnames(
                    mailListHeaderContainer,
                    isRPDenselessEnabled &&
                        isBleedThroughEnabled &&
                        mailListHeaderContainerBleedThrough
                )}
                ref={headerRef}
            >
                {props.renderFirstRow?.(mailListFirstRow)}
                <div
                    className={classnames(
                        mailListSecondRow,
                        isRPDenselessEnabled &&
                            (isSearchTable
                                ? mailSearchSecondRowDenseless
                                : mailListSecondRowDenseless),
                        isSingleLineView ? mcl : scl,
                        senderImageEnabled ? senderImageOn : senderImageOff,
                        shouldApplyNewNoCheckboxStyles &&
                            (senderImageEnabled
                                ? triMlCheckboxBehaviorSenderImageOn
                                : triMlCheckboxBehaviorSenderImageOff),
                        isCheckboxSpaceReserved && checkboxSpaceReserved,
                        isInboxWithPivots ? showInboxPivots : noInboxPivots,
                        isStickyNotesHeader && stickyNotesHeaderPadding,
                        thirdRow && thirdrowPaddingBottom,
                        firstRow && withFirstRow,
                        isCopilotResultsTabVisible(mailboxInfo) && copilotResultsTabVisible,
                        densityModeClass,
                        isRPDenselessEnabled && densityModeString === 'compact' && compactDenseless,
                        showFullLabels
                            ? mlHeaderButtonsFull
                            : showSemiCompactLabels
                            ? mlHeaderButtonsSemiCompact
                            : mlHeaderButtonsCompact,
                        isRPDenselessEnabled && monTypeRampStyles
                    )}
                >
                    <div
                        className={classnames(
                            mailListSecondRowInnerContent,
                            showFullLabels && mailListSecondRowCompact,
                            showSemiCompactLabels && mailListSecondRowSemiCompact,
                            (isBizchatMLInterplayEnabled || !props.shouldShowFilterMenu) &&
                                mailListSecondRowInterplayEnabled,
                            isSearchTable && mailListSecondRowInnerContentSearchTable
                        )}
                    >
                        {!isCatchUpOrHeadlinesFilterSelected &&
                            ((showMultiSelectButton &&
                                (checkedStateMailList || !props.shouldShowFilterMenu)) ||
                                showCheckboxForReselectFlight ||
                                !showMultiSelectButton) &&
                            renderSecondRowLeftEntity(isInboxWithPivots, isStickyNotesHeader)}
                        {((showMultiSelectButton && !checkedStateMailList) ||
                            isEmptyFolder ||
                            !showMultiSelectButton) &&
                            props.renderSecondRowCustomContent(
                                classnames(
                                    isStickyNotesHeader
                                        ? notesHeaderSecondRow
                                        : mailListSecondRowCustomContainer,
                                    classNameForUserSettings,
                                    secondRowCustomContainer,
                                    densityModeClass
                                )
                            )}
                        {showMultiSelectButton &&
                            checkedStateMailList &&
                            !isEmptyFolder &&
                            props.renderSecondRowCustomContent(
                                classnames(
                                    notesHeaderSecondRow,
                                    selectAllHeaderStyle,
                                    densityModeClass
                                )
                            )}
                        {showMultiSelectButton &&
                            props.shouldShowFilterMenu &&
                            !isEmptyFolder &&
                            !isCatchUpOrHeadlinesFilterSelected && (
                                <MailListHeaderIconContainer
                                    tableView={tableView}
                                    supportedViewFilters={getViewFilters()}
                                    filterMenuSource={props.filterMenuSource}
                                    tableViewId={props.tableViewId}
                                    showFullLabels={showFullLabels}
                                    showSemiCompactLabels={showSemiCompactLabels}
                                    isBleedThrough={isRPDenselessEnabled}
                                />
                            )}
                        {(!showMultiSelectButton || isEmptyFolder) &&
                            !isCatchUpOrHeadlinesFilterSelected &&
                            props.shouldShowFilterMenu &&
                            !isCopilotResultsTableSelected &&
                            props.filterMenuSource !== 'GroupHeader' && (
                                <div
                                    className={classnames(
                                        headerIconContainer,
                                        isRPDenselessEnabled && headerIconContainerBleedThrough,
                                        getDensityModeCssClass(
                                            headerIconContainerFull,
                                            headerIconContainerMediumOrCompact,
                                            headerIconContainerMediumOrCompact
                                        )
                                    )}
                                >
                                    {!shouldHideNonPriorityHeaderButtons &&
                                        !isInNeedsReplyView &&
                                        shouldShowJumpTo(tableView) && (
                                            <MailListJumpToPopover
                                                tableViewId={props.tableViewId}
                                                showButtonLabel={showFullLabels}
                                                isBleedThrough={isRPDenselessEnabled}
                                            />
                                        )}
                                    {!isInNeedsReplyView && renderMailListFilter()}
                                    {!shouldHideNonPriorityHeaderButtons && !isInNeedsReplyView && (
                                        <MailListSortMenuV2
                                            sortMenuSource={props.filterMenuSource}
                                            tableViewId={props.tableViewId}
                                            showButtonLabel={
                                                showSemiCompactLabels || showFullLabels
                                            }
                                            isBleedThrough={isRPDenselessEnabled}
                                        />
                                    )}
                                    {shouldShowPrioritize && (
                                        <PriorityViewHeaderButtons
                                            tableViewId={props.tableViewId}
                                            isBleedThrough={isRPDenselessEnabled}
                                            showButtonLabel={showFullLabels}
                                        />
                                    )}
                                    {isInNeedsReplyView && (
                                        <NeedsReplyViewHeaderButtons
                                            isBleedThrough={isRPDenselessEnabled}
                                            showButtonLabel={showFullLabels}
                                        />
                                    )}
                                </div>
                            )}
                    </div>
                </div>
                {isInbox && isPrioritizeActive && (
                    <PrioritizeStatusBar tableViewId={props.tableViewId} />
                )}

                {isInbox && !isPrioritizeActive && (
                    <MailListNudgeContainer tableViewId={props.tableViewId} />
                )}

                {isPriorityView(tableView?.tableQuery as MailFolderTableQuery) && (
                    <PriorityViewBar tableViewId={props.tableViewId} />
                )}

                {isInNeedsReplyView && <NeedsReplyBar />}

                {thirdRow}
                <CollapseDelayed visible={props.renderBulkActionProgressBar !== undefined}>
                    <div
                        className={classnames(
                            bulkActionBarPaddingRuleProgress,
                            classNameForUserSettings,
                            isSingleLineView && !isItemView && conversationViewMCL
                        )}
                    >
                        {props.renderBulkActionProgressBar?.()}
                    </div>
                </CollapseDelayed>
            </div>
        );
    },
    'MailListHeader'
);
export default defaultExport;

function getViewFilters(): ViewFilter[] {
    const viewFilters: ViewFilter[] = [
        'All',
        'Unread',
        'Flagged',
        'ToOrCcMe',
        'HasFile',
        'Mentioned',
        'HasCalendarInvite',
    ];
    return viewFilters;
}
